import React, { useState, useEffect } from 'react';
import { TextField, Button, Grid, Typography, MenuItem, Card, CardContent, CardActions,Container,Alert } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from './firebase'; // Adjust the import according to your Firebase config
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Layout from './Layout';
import { useAppContext } from './AppContext';

const PromotionCodes = () => {
  const [promotionCodes, setPromotionCodes] = useState([]);
  const [newPromotion, setNewPromotion] = useState({
    title: '',
    startDate: null,
    endDate: null,
    discountType: 'amount',
    amountValue: '',
  });
  const [isOffline, setIsOffline] = useState(false);
  const { t } = useAppContext();

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    // Add event listeners for online and offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check initial status
    setIsOffline(!navigator.onLine);

    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    // Fetch existing promotion codes from Firestore
    const fetchPromotionCodes = async () => {
      const querySnapshot = await getDocs(collection(db, 'promotionCodes'));
      const codes = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPromotionCodes(codes);
    };
    fetchPromotionCodes();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPromotion({ ...newPromotion, [name]: value });
  };

  const handleDateChange = (name, date) => {
    setNewPromotion({ ...newPromotion, [name]: date });
  };

  const handleSubmit = async () => {
    try {
      if (newPromotion.id) {
        // Update existing promotion code
        const promotionRef = doc(db, 'promotionCodes', newPromotion.id);
        await updateDoc(promotionRef, newPromotion);
      } else {
        // Create new promotion code
        await addDoc(collection(db, 'promotionCodes'), newPromotion);
      }
      setNewPromotion({ title: '', startDate: null, endDate: null, discountType: 'amount', amountValue: '' });
      // Refresh the list
      const querySnapshot = await getDocs(collection(db, 'promotionCodes'));
      const codes = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPromotionCodes(codes);
    } catch (error) {
      console.error('Error adding or updating document: ', error);
    }
  };

  const handleEdit = (code) => {
    setNewPromotion(code);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'promotionCodes', id));
      setPromotionCodes(promotionCodes.filter(code => code.id !== id));
    } catch (error) {
      console.error('Error deleting document: ', error);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Layout>
    <Container>   
    {isOffline && (
            <Alert severity="warning" sx={{ marginTop: '10px' }}>
              {t("You are offline. Some features may not be available.")}
            </Alert>
          )}
    <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h4" mt={3}>{t("Manage Promotion Codes")}</Typography>
                </Grid>

                {/* Form to add/edit promotion code */}
                <Grid item xs={12} md={6}>
                    <Card variant='outlined'>
                    <CardContent>
                    <Grid container spacing={2}>
                <Grid item xs={12}>
                <TextField
                    fullWidth
                    label={t("Promotion Code Title")}
                    name="title"
                    value={newPromotion.title}
                    onChange={handleInputChange}
                />
                </Grid>
                <Grid item xs={6}>
                <DatePicker
                    label={t("Start Date")}
                    value={newPromotion.startDate}
                    onChange={(date) => handleDateChange('startDate', date)}
                    renderInput={(params) => <TextField fullWidth {...params} />}
                    slotProps={{ textField: { fullWidth: true } }}
                />
                </Grid>
                <Grid item xs={6}>
                <DatePicker
                    label={t("End Date")}
                    value={newPromotion.endDate}
                    onChange={(date) => handleDateChange('endDate', date)}
                    renderInput={(params) => <TextField fullWidth {...params} />}
                    slotProps={{ textField: { fullWidth: true } }}
                />
                </Grid>
                <Grid item xs={6}>
                <TextField
                    select
                    fullWidth
                    label={t("Discount Type")}
                    name="discountType"
                    value={newPromotion.discountType}
                    onChange={handleInputChange}
                >
                    <MenuItem value="amount">{t("Amount")}</MenuItem>
                    <MenuItem value="percentage">{t("Percentage")}</MenuItem>
                </TextField>
                </Grid>
                <Grid item xs={6}>
                <TextField
                    fullWidth
                    label={t("Amount Value")}
                    name="amountValue"
                    value={newPromotion.amountValue}
                    onChange={handleInputChange}
                    type="number"
                />
                </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Button variant="contained" color="primary" onClick={handleSubmit} disableElevation>
              {newPromotion.id ? t("Update Promotion") : t("Create Promotion")}
            </Button>
          </CardActions>
        </Card>
      </Grid>

      {/* Display existing promotion codes */}
      <Grid item xs={12} md={6}>
        {promotionCodes.map((code) => (
          <Card key={code.id} style={{ marginBottom: '16px' }} variant='outlined'>
            <CardContent>
              <Typography variant="h6">{code.title}</Typography>
              <Typography>{t("Start Date")}: {code.startDate.toDate().toLocaleDateString()}</Typography>
              <Typography>{t("End Date")}: {code.endDate.toDate().toLocaleDateString()}</Typography>
              <Typography>{t("Discount")}: {code.discountType === 'amount' ? `${code.amountValue} MMK` : `${code.amountValue}%`}</Typography>
            </CardContent>
            <CardActions>
              <Button color="primary" onClick={() => handleEdit(code)}>{t("Edit")}</Button>
              <Button color="secondary" onClick={() => handleDelete(code.id)}>{t("Delete")}</Button>
            </CardActions>
          </Card>
        ))}
      </Grid>
    </Grid>
    </Container>
    </Layout>
    </LocalizationProvider>
  );
};

export default PromotionCodes;
